import {
  ChakraBox,
  ChakraFlex,
  InputGroupV2,
  PhoneNumbersV2,
  TextInputV2,
  UniversalSelect,
} from '@equip.health/ui';

import { validateEmail } from '~/lib/common';
import { EDITBASICINFO_PHONES_REQUIRED_FIELDS } from '~/lib/constants/patients.constants';
import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';
import { patientEnumMenuOptionsMapper } from '~/lib/util/patient.util';
import {
  ERROR_MESSAGES,
  getMultiSelectedUniversalSelectOptions,
} from '~/lib/util/profile.util';
import {
  useGetContactPreferencesEnumQuery,
  useGetProviderLanguagesEnumQuery,
  useGetRelationsToPatientEnumQuery,
} from '~/store/services/enumerations';

import SupportLinkedAccountsText from '~/components/common/SupportLinkedAccountsText';
import { EMAIL_ERROR_MESSAGES } from '~/lib/util/patientContact.util';
import { toMultiSelectOption } from '~/lib/util/menu';
import { PhoneFieldValue } from '~/lib/types/patient/PhoneFieldValue';

const TOP_LEVEL_ID = 'contact-info';

interface ContactInfoSectionProps {
  isSupport?: boolean;
  contactInfo: ContactInfoType;
  phoneData: PhonesFieldType;
  isGrantEquipAccess: boolean;
  otherLinkedPatients: SupportOtherLinkedPatient[];
  isEmailSameAsExistingSupportEmail: boolean;
  isEmailSameAsPatientEmail: boolean;
  isEmergencyContact: boolean;
  updateField: (field: string, value: boolean) => void;
  updateInvalidFields: (
    section: 'contactInfo',
    field: string,
    invalid: boolean,
  ) => void;
  updateSection: (
    section: 'contactInfo' | 'phonesField',
    field: keyof ContactInfoType | keyof PhonesFieldType,
    value: string | string[] | PhoneFieldValue | boolean,
  ) => void;
}

const ContactInfoSection = ({
  isSupport,
  contactInfo,
  isGrantEquipAccess,
  isEmailSameAsExistingSupportEmail,
  isEmailSameAsPatientEmail,
  isEmergencyContact,
  phoneData,
  otherLinkedPatients,
  updateField,
  updateInvalidFields,
  updateSection,
}: ContactInfoSectionProps) => {
  const {
    data: contactPreferences,
    isLoading: isGetContactPreferencesLoading,
  } = useGetContactPreferencesEnumQuery();
  const contactPreferencesOptions =
    contactPreferences?.map(toMultiSelectOption) || [];

  const { data: referralRelations, isLoading: isRelationsToPatientLoading } =
    useGetRelationsToPatientEnumQuery();
  const relationOptions = patientEnumMenuOptionsMapper(referralRelations);

  const { data: languages, isLoading: isGetLanguagesLoading } =
    useGetProviderLanguagesEnumQuery();
  const languagesOptions = patientEnumMenuOptionsMapper(languages);

  const shouldShowEquipAppEmailError = isGrantEquipAccess && !contactInfo.email;

  const emailFieldErrorMessage = () => {
    if (isEmailSameAsPatientEmail)
      return EMAIL_ERROR_MESSAGES.PATIENT_EMAIL_MATCH;
    if (isEmailSameAsExistingSupportEmail)
      return EMAIL_ERROR_MESSAGES.SUPPORT_EMAIL_MATCH;
    if (shouldShowEquipAppEmailError)
      return EMAIL_ERROR_MESSAGES.SUPPORT_REQUIRED;
    return '';
  };

  const validateField = (
    field: keyof ContactInfoType,
    value: string,
  ): boolean => {
    switch (field) {
      case 'email':
        return !value || validateEmail(String(value));

      default:
        return true;
    }
  };

  const handleFieldChange = (
    field: keyof ContactInfoType,
    value: string | string[],
  ) => {
    if (field === 'email' && contactInfo.isDuplicateEmail) {
      updateSection('contactInfo', 'isDuplicateEmail', false);
    }
    const isFieldValid = validateField(field, String(value));
    updateInvalidFields('contactInfo', field, !isFieldValid);
    updateSection('contactInfo', field, value);
  };

  const handlePhoneChange = (pfv: PhoneFieldValue) => {
    const [key, value] = Object.entries(pfv)[0];
    updateSection('phonesField', key as keyof PhonesFieldType, value);
  };
  return (
    <ChakraFlex flexDirection="column" gap="18px">
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__relation-label`}
        label="Relation to patient"
        shouldShowAsteriskWhenRequired
        flexGrow={0}
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__relation`}
          isInvalid={contactInfo.invalidFields.relationToPatient}
          isLoading={isRelationsToPatientLoading}
          isSingleSelect
          onChange={(value) => {
            handleFieldChange('relationToPatient', value?.value);
          }}
          options={relationOptions}
          value={getSelectedUniversalSelectOption(
            contactInfo.relationToPatient,
            relationOptions,
          )}
        />
      </InputGroupV2>
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__preferred-contact-method`}
        label="Preferred contact method"
        flexGrow={0}
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__preferred-contact-method-select`}
          isLoading={isGetContactPreferencesLoading}
          isSingleSelect={false}
          onChange={(options) => {
            const selectedValues = options.map((option) => option.value);
            handleFieldChange('contactPreference', selectedValues);
          }}
          options={contactPreferencesOptions}
          value={getMultiSelectedUniversalSelectOptions(
            contactInfo.contactPreference,
            contactPreferencesOptions,
          )}
          withCheckBox
        />
      </InputGroupV2>
      <ChakraBox>
        <InputGroupV2
          id={`${TOP_LEVEL_ID}__email`}
          label="Email address"
          shouldShowAsteriskWhenRequired={isGrantEquipAccess}
          value={
            (contactInfo.email &&
              contactInfo.invalidFields.email &&
              ERROR_MESSAGES.EMAIL) ||
            (contactInfo.email &&
              contactInfo.isDuplicateEmail &&
              'Duplicate email found') ||
            emailFieldErrorMessage()
          }
          flexGrow={0}
        >
          <TextInputV2
            id={`${TOP_LEVEL_ID}__email-input`}
            isInvalid={
              contactInfo.invalidFields.email ||
              contactInfo.isDuplicateEmail ||
              shouldShowEquipAppEmailError ||
              isEmailSameAsExistingSupportEmail ||
              isEmailSameAsPatientEmail
            }
            onChange={(value) => handleFieldChange('email', value)}
            value={contactInfo.email}
          />
        </InputGroupV2>
        <SupportLinkedAccountsText
          linkedAccountsCount={
            otherLinkedPatients ? otherLinkedPatients.length : 0
          }
          onClick={() => updateField('isLinkedAccountsModalVisible', true)}
          paddingTop="6px"
          topLevelId={`${TOP_LEVEL_ID}__email-field`}
        />
      </ChakraBox>
      <PhoneNumbersV2
        id={`${TOP_LEVEL_ID}__phones`}
        mobileRequired={isGrantEquipAccess || isSupport || isEmergencyContact}
        phonesData={phoneData}
        requiredFields={EDITBASICINFO_PHONES_REQUIRED_FIELDS}
        updatePhonesField={(pfv: PhoneFieldValue) => handlePhoneChange(pfv)}
      />
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__languages-spoken`}
        label="Language(s) spoken"
        flexGrow={0}
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__languages-spoken-select`}
          isLoading={isGetLanguagesLoading}
          isSingleSelect={false}
          onChange={(options) => {
            const selectedValues = options.map((option) => option.value);
            handleFieldChange('languages', selectedValues);
          }}
          options={languagesOptions}
          value={getMultiSelectedUniversalSelectOptions(
            contactInfo.languages,
            languagesOptions,
          )}
          withCheckBox
        />
      </InputGroupV2>
    </ChakraFlex>
  );
};

export default ContactInfoSection;
